/**
 * SVG serializer. Consumes the same ops as the canvas painter, always at zoom 1
 * so exports are canonical.
 *
 * Two things here are bug fixes waiting to happen if removed:
 *  - fill="none" is written explicitly. SVG's default fill is BLACK, so omitting
 *    it exports every outlined shape as a solid black box.
 *  - Text is one <tspan> per line with absolute x/y, never a dy chain, so the
 *    line breaks measured here are frozen into the file.
 */

import { FONT_STACKS, type Drawable, type MeasureText, type PathCmd } from '../geometry/drawable';
import { drawableOf } from '../geometry/cache';
import { unionRects, worldBounds } from '../geometry/transform';
import type { SlateElement } from '../scene/types';

export interface SvgOptions {
  readonly padding?: number;
  readonly background?: string | null;
}

function r(n: number): number {
  return Math.round(n * 100) / 100;
}

export function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

export function pathToD(cmds: readonly PathCmd[]): string {
  const parts: string[] = [];
  for (const cmd of cmds) {
    switch (cmd.c) {
      case 'M':
        parts.push(`M ${r(cmd.p[0])} ${r(cmd.p[1])}`);
        break;
      case 'L':
        parts.push(`L ${r(cmd.p[0])} ${r(cmd.p[1])}`);
        break;
      case 'C':
        parts.push(
          `C ${r(cmd.c1[0])} ${r(cmd.c1[1])} ${r(cmd.c2[0])} ${r(cmd.c2[1])} ${r(cmd.p[0])} ${r(cmd.p[1])}`,
        );
        break;
      case 'Z':
        parts.push('Z');
        break;
    }
  }
  return parts.join(' ');
}

function elementTransform(el: SlateElement): string {
  const parts = [`translate(${r(el.x)} ${r(el.y)})`];
  if (el.angle !== 0) {
    const deg = Math.round(((el.angle * 180) / Math.PI) * 10000) / 10000;
    // Twin of the canvas translate/rotate/translate sequence: SVG bakes the
    // rotation centre into the rotate() call instead.
    parts.push(`rotate(${deg} ${r(el.width / 2)} ${r(el.height / 2)})`);
  }
  return parts.join(' ');
}

function opsToSvg(drawable: Drawable): string {
  const out: string[] = [];
  const alpha = drawable.opacity;
  for (const op of drawable.ops) {
    if (op.k === 'path') {
      const attrs: string[] = [`d="${pathToD(op.cmds)}"`];
      // Explicit, always. See the note at the top of this file.
      attrs.push(op.fill ? `fill="${escapeXml(op.fill.color)}"` : 'fill="none"');
      if (op.fill && alpha < 1) attrs.push(`fill-opacity="${r(alpha)}"`);
      if (op.stroke && op.stroke.width > 0) {
        attrs.push(`stroke="${escapeXml(op.stroke.color)}"`);
        attrs.push(`stroke-width="${r(op.stroke.width)}"`);
        attrs.push(`stroke-linecap="${op.stroke.cap}"`);
        attrs.push(`stroke-linejoin="${op.stroke.join}"`);
        if (op.stroke.dash) attrs.push(`stroke-dasharray="${op.stroke.dash.map(r).join(' ')}"`);
        if (alpha < 1) attrs.push(`stroke-opacity="${r(alpha)}"`);
      }
      out.push(`    <path ${attrs.join(' ')} />`);
      continue;
    }
    const lines = op.lines
      .filter((line) => line.text.length > 0)
      .map((line) => `<tspan x="${r(line.x)}" y="${r(line.y)}">${escapeXml(line.text)}</tspan>`)
      .join('');
    if (!lines) continue;
    const attrs = [
      'xml:space="preserve"',
      `font-family="${escapeXml(op.font.family)}"`,
      `font-size="${r(op.font.size)}px"`,
      `font-weight="${op.font.weight}"`,
      `fill="${escapeXml(op.color)}"`,
      'dominant-baseline="alphabetic"',
    ];
    if (alpha < 1) attrs.push(`fill-opacity="${r(alpha)}"`);
    out.push(`    <text ${attrs.join(' ')}>${lines}</text>`);
  }
  return out.join('\n');
}

export function toSvgString(
  elements: readonly SlateElement[],
  measure: MeasureText,
  opts: SvgOptions = {},
): string {
  const padding = opts.padding ?? 24;
  const live = elements.filter((el) => !el.isDeleted);
  const bounds = unionRects(live.map(worldBounds)) ?? { x: 0, y: 0, width: 100, height: 100 };
  const width = Math.max(1, bounds.width + padding * 2);
  const height = Math.max(1, bounds.height + padding * 2);
  const minX = bounds.x - padding;
  const minY = bounds.y - padding;

  const body: string[] = [];
  for (const el of live) {
    if (el.width === 0 && el.height === 0 && el.type !== 'text') continue;
    const svg = opsToSvg(drawableOf(el, measure));
    if (!svg) continue;
    body.push(`  <g transform="${elementTransform(el)}">\n${svg}\n  </g>`);
  }

  const background = opts.background
    ? `  <rect x="${r(minX)}" y="${r(minY)}" width="${r(width)}" height="${r(
        height,
      )}" fill="${escapeXml(opts.background)}" />\n`
    : '';

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${r(width)}" height="${r(height)}" viewBox="${r(
    minX,
  )} ${r(minY)} ${r(width)} ${r(height)}">
  <!-- Exported by Slate Whiteboard. Text is laid out as measured at export
       time; a viewer without these fonts changes glyph widths, not line breaks. -->
  <style>
    text { font-family: ${FONT_STACKS.sans}; }
    text.mono { font-family: ${FONT_STACKS.mono}; }
  </style>
${background}${body.join('\n')}
</svg>
`;
}
